import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Collapse,
  useTheme as useMuiTheme,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Divider,
  TextField,
  InputAdornment,
  Stack,
  CircularProgress,
  Alert,
  Button,
  Badge
} from '@mui/material';
import { useTheme } from '../context/ThemeContext';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  BarChart as BarChartIcon,
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { syncData, getSyncStats } from '../services/DataSyncService';

const TreeDataDisplay = () => {
  const { darkMode } = useTheme();
  const muiTheme = useMuiTheme();
  const [treeRecords, setTreeRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [expandedRows, setExpandedRows] = useState({});
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [clientStats, setClientStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncStats, setSyncStats] = useState({ total: 0, synced: 0, unsynced: 0 });
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState('');
  const [syncError, setSyncError] = useState('');
  const [networkStatus, setNetworkStatus] = useState(navigator.onLine);

  useEffect(() => {
    // Load tree records and clients from localStorage
    loadData();
    
    // Setup network status listeners
    const handleOnlineStatus = () => {
      setNetworkStatus(navigator.onLine);
    };
    
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);
    
    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  useEffect(() => {
    // Apply filters when selectedClient or searchTerm changes
    filterRecords();
  }, [selectedClient, searchTerm, treeRecords]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Import API services
      const { treeAPI, clientAPI } = await import('../services/ApiService');
      
      // Load clients first
      const clientsData = await clientAPI.getAllClients();
      setClients(clientsData);
      
      // Load tree records
      const treesData = await treeAPI.getAllTrees();
      // Sort by timestamp (newest first)
      treesData.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setTreeRecords(treesData);
      
      // Calculate statistics by client
      calculateClientStats(treesData, clientsData);
      
      // Update sync stats
      setSyncStats(getSyncStats());
    } catch (error) {
      console.error('Error loading data:', error);
      setSyncError('Failed to load data from server');
    } finally {
      setLoading(false);
    }
  };

  const calculateClientStats = (records, clientsList) => {
    const stats = {};
    
    // Initialize stats for each client
    clientsList.forEach(client => {
      stats[client.id] = { 
        totalTrees: 0, 
        averageSexRatio: 0,
        minSexRatio: 1,
        maxSexRatio: 0,
        lastRecord: null,
        totalMale: 0,
        totalFemale: 0,
        distribution: { low: 0, medium: 0, high: 0 },
        recordCount: 0,
        latestScan: 'Never'
      };
    });
    
    // Calculate stats from records
    records.forEach(record => {
      if (record.clientId && stats[record.clientId]) {
        const stat = stats[record.clientId];
        const sexRatio = parseFloat(record.sexRatio);
        
        stat.totalTrees++;
        stat.totalMale += parseInt(record.maleFlowers);
        stat.totalFemale += parseInt(record.femaleFlowers);
        stat.recordCount++;
        
        // Update distribution counts
        if (!isNaN(sexRatio)) {
          if (sexRatio <= 0.3) stat.distribution.low++;
          else if (sexRatio <= 0.5) stat.distribution.medium++;
          else stat.distribution.high++;
        }
        
        if (sexRatio < stat.minSexRatio) stat.minSexRatio = sexRatio;
        if (sexRatio > stat.maxSexRatio) stat.maxSexRatio = sexRatio;
        
        // Track the most recent record
        if (!stat.lastRecord || new Date(record.timestamp) > new Date(stat.lastRecord.timestamp)) {
          stat.lastRecord = record;
          stat.latestScan = formatDate(record.timestamp);
        }
      }
    });
    
    // Calculate averages
    Object.keys(stats).forEach(clientId => {
      const stat = stats[clientId];
      if (stat.totalTrees > 0) {
        // Calculate overall sex ratio from total flowers
        stat.averageSexRatio = stat.totalFemale / (stat.totalFemale + stat.totalMale);
        stat.avgSexRatio = stat.averageSexRatio.toFixed(3);
      }
    });
    
    setClientStats(stats);
  };

  const filterRecords = () => {
    let filtered = [...treeRecords];
    
    // Filter by client
    if (selectedClient !== 'all') {
      filtered = filtered.filter(record => record.clientId === selectedClient);
    }
    
    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(record => 
        record.treeId.toLowerCase().includes(search) ||
        (record.clientName && record.clientName.toLowerCase().includes(search)) ||
        (record.recordedBy && record.recordedBy.toLowerCase().includes(search))
      );
    }
    
    setFilteredRecords(filtered);
    setPage(0); // Reset to first page when filtering
  };

  const handleClientChange = (event) => {
    setSelectedClient(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getRatioColor = (ratio) => {
    // Handle undefined, null or invalid ratio values
    if (ratio === undefined || ratio === null || isNaN(ratio)) {
      return 'default';
    }
    
    const ratioNum = parseFloat(ratio);
    if (ratioNum <= 0.3) return 'error';
    if (ratioNum <= 0.5) return 'warning';
    return 'success';
  };

  const handleExpandRow = (recordId) => {
    setExpandedRows(prev => ({
      ...prev,
      [recordId]: !prev[recordId]
    }));
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Unknown';
  };

  // Render client statistics cards
  const renderClientStatistics = () => {
    // If filtering by a specific client, only show that client's stats
    const clientsToShow = selectedClient !== 'all' 
      ? clients.filter(c => c.id === selectedClient) 
      : clients;
    
    return (
      <>
        <Typography variant="h6" gutterBottom>
          Client Statistics
        </Typography>
        <Grid container spacing={2}>
          {clientsToShow.map(client => {
            const stats = clientStats[client.id] || { 
              totalTrees: 0, 
              averageSexRatio: 0, 
              minSexRatio: 0, 
              maxSexRatio: 0 
            };
            
            return (
              <Grid item xs={12} md={selectedClient !== 'all' ? 12 : 6} key={client.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <BarChartIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="subtitle1" fontWeight="bold">
                        {client.name}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">Trees Scanned</Typography>
                        <Typography variant="h6">{stats.totalTrees}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">Avg Sex Ratio</Typography>
                        <Chip 
                          label={stats.averageSexRatio ? stats.averageSexRatio.toFixed(3) : "N/A"} 
                          color={getRatioColor(stats.averageSexRatio)}
                          size="small"
                          sx={{ fontWeight: 'bold', mt: 0.5 }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">Min Ratio</Typography>
                        <Typography variant="h6">
                          {stats.minSexRatio < 1 ? stats.minSexRatio.toFixed(3) : "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">Max Ratio</Typography>
                        <Typography variant="h6">
                          {stats.maxSexRatio > 0 ? stats.maxSexRatio.toFixed(3) : "N/A"}
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    {stats.lastRecord && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Last Record: {formatDate(stats.lastRecord.timestamp)}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </>
    );
  };

  const handleSyncData = () => {
    if (!networkStatus) {
      setSyncError('Cannot sync while offline');
      setTimeout(() => setSyncError(''), 3000);
      return;
    }
    
    setSyncLoading(true);
    setSyncError('');
    setSyncSuccess('');
    
    syncData()
      .then(result => {
        setSyncSuccess(result.message);
        loadData(); // Reload data to get updated sync status
        setTimeout(() => setSyncSuccess(''), 3000);
      })
      .catch(error => {
        setSyncError(error.message);
        setTimeout(() => setSyncError(''), 5000);
      })
      .finally(() => {
        setSyncLoading(false);
      });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (treeRecords.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No tree records found. Start scanning trees to collect data.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Tree Data Records</Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {syncStats.synced}/{syncStats.total} records synced
          </Typography>
          
          <Badge badgeContent={syncStats.unsynced} color="error" sx={{ mr: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={networkStatus ? <CloudUploadIcon /> : <CloudOffIcon />}
              onClick={handleSyncData}
              disabled={!networkStatus || syncLoading || syncStats.unsynced === 0}
            >
              {syncLoading ? 'Syncing...' : 'Sync Data'}
            </Button>
          </Badge>
        </Box>
      </Box>
      
      {syncSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {syncSuccess}
        </Alert>
      )}
      
      {syncError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {syncError}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="client-select-label">Client</InputLabel>
              <Select
                labelId="client-select-label"
                id="client-select"
                value={selectedClient}
                label="Client"
                onChange={handleClientChange}
              >
                <MenuItem value="all">All Clients</MenuItem>
                {clients.map(client => (
                  <MenuItem key={client.id} value={client.id}>{client.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by tree ID or client name..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Client Statistics */}
      {selectedClient !== 'all' && clientStats[selectedClient] && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <BarChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {getClientName(selectedClient)} - Statistics
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">Records</Typography>
                <Typography variant="h5">{clientStats[selectedClient].recordCount}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">Avg. Sex Ratio</Typography>
                <Typography variant="h5">
                  <Chip 
                    label={clientStats[selectedClient].avgSexRatio} 
                    color={getRatioColor(clientStats[selectedClient].avgSexRatio)} 
                    size="small" 
                    sx={{ fontWeight: 'bold' }}
                  />
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">Distribution</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title="Low ratio (<0.4)">
                    <Chip 
                      label={clientStats[selectedClient].distribution.low} 
                      color="error" 
                      size="small" 
                    />
                  </Tooltip>
                  <Tooltip title="Medium ratio (0.4-0.6)">
                    <Chip 
                      label={clientStats[selectedClient].distribution.medium} 
                      color="warning" 
                      size="small" 
                    />
                  </Tooltip>
                  <Tooltip title="High ratio (>0.6)">
                    <Chip 
                      label={clientStats[selectedClient].distribution.high} 
                      color="success" 
                      size="small" 
                    />
                  </Tooltip>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">Latest Scan</Typography>
                <Typography variant="body1">{clientStats[selectedClient].latestScan}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Summary of all clients when "All Clients" is selected */}
      {selectedClient === 'all' && Object.keys(clientStats).length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <BarChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Client Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell align="center">Records</TableCell>
                    <TableCell align="center">Avg. Sex Ratio</TableCell>
                    <TableCell align="center">Distribution</TableCell>
                    <TableCell align="right">Latest Scan</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.keys(clientStats).map(clientId => (
                    <TableRow key={clientId} hover>
                      <TableCell>{getClientName(clientId)}</TableCell>
                      <TableCell align="center">{clientStats[clientId].recordCount}</TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={clientStats[clientId].avgSexRatio} 
                          color={getRatioColor(clientStats[clientId].avgSexRatio)} 
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="Low">
                            <Chip label={clientStats[clientId].distribution.low} color="error" size="small" />
                          </Tooltip>
                          <Tooltip title="Medium">
                            <Chip label={clientStats[clientId].distribution.medium} color="warning" size="small" />
                          </Tooltip>
                          <Tooltip title="High">
                            <Chip label={clientStats[clientId].distribution.high} color="success" size="small" />
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{clientStats[clientId].latestScan}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {filteredRecords.length > 0 ? (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="tree records table">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Tree ID</TableCell>
                <TableCell align="right">Male</TableCell>
                <TableCell align="right">Female</TableCell>
                <TableCell align="center">Sex Ratio</TableCell>
                <TableCell align="center">Sync Status</TableCell>
                <TableCell align="right">Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRecords
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((record) => (
                  <React.Fragment key={record.id}>
                    <TableRow hover>
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          onClick={() => handleExpandRow(record.id)}
                          aria-label="expand row"
                        >
                          {expandedRows[record.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{getClientName(record.clientId)}</TableCell>
                      <TableCell>{record.treeId}</TableCell>
                      <TableCell align="right">{record.maleFlowers}</TableCell>
                      <TableCell align="right">{record.femaleFlowers}</TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={record.sexRatio} 
                          color={getRatioColor(record.sexRatio)} 
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={record.synced ? "Synchronized with server" : "Waiting to sync"}>
                          {record.synced ? (
                            <CloudDoneIcon color="success" fontSize="small" />
                          ) : (
                            <CloudOffIcon color="warning" fontSize="small" />
                          )}
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">{formatDate(record.timestamp)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                        <Collapse in={expandedRows[record.id]} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1, py: 1 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle2" component="div" gutterBottom>
                                  Location Information
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <LocationIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                                  {record.latitude && record.longitude ? (
                                    <>
                                      <Typography variant="body2" sx={{ mr: 1 }}>
                                        Latitude: {record.latitude}, Longitude: {record.longitude}
                                      </Typography>
                                      <Tooltip title="View on Google Maps">
                                        <IconButton 
                                          size="small" 
                                          color="primary"
                                          component="a"
                                          href={`https://www.google.com/maps/search/?api=1&query=${record.latitude},${record.longitude}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          <LocationIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No location data available
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle2" component="div" gutterBottom>
                                  Client Information
                                </Typography>
                                {record.clientId ? (
                                  <>
                                    {clients.find(c => c.id === record.clientId) ? (
                                      <ClientInfoSummary client={clients.find(c => c.id === record.clientId)} />
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        Client details not available
                                      </Typography>
                                    )}
                                  </>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No client associated with this record
                                  </Typography>
                                )}
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle2" component="div" gutterBottom>
                                  Sync Information
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <Typography variant="body2">
                                    <strong>Status:</strong> {record.synced ? 'Synchronized' : 'Pending'}
                                  </Typography>
                                  {record.synced && record.syncedAt && (
                                    <Typography variant="body2">
                                      <strong>Synced at:</strong> {formatDate(record.syncedAt)}
                                    </Typography>
                                  )}
                                  {!record.synced && (
                                    <Typography variant="body2" color="text.secondary">
                                      This record will be synchronized with the server when online.
                                    </Typography>
                                  )}
                                  {record.syncAttempts > 0 && (
                                    <Typography variant="body2">
                                      <strong>Sync attempts:</strong> {record.syncAttempts}
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredRecords.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      ) : (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1">No tree records found</Typography>
          {searchTerm && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try changing your search term or filters
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};

// Helper component to display a summary of client information
const ClientInfoSummary = ({ client }) => {
  return (
    <Box>
      <Typography variant="body2">
        <strong>Type:</strong> {client.plantationType || 'Not specified'}
      </Typography>
      <Typography variant="body2">
        <strong>Soil:</strong> {client.soilProfile || 'Not specified'}
      </Typography>
      <Typography variant="body2">
        <strong>Fertilizer:</strong> {client.fertilizerType || 'Not specified'}
      </Typography>
      <Typography variant="body2">
        <strong>Plantation Age:</strong> {client.plantationAge ? `${client.plantationAge} years` : 'Not specified'}
      </Typography>
    </Box>
  );
};

export default TreeDataDisplay; 